import type { OptionsDocument, QuoteDocument } from 'paintscout'
import type { QuoteItemSection, QuoteItemView } from '../types'
import { getDetails } from '../details/get-details/get-details'
import { getItems } from '../items/get-items/get-items/get-items'
import { generateProductDescription } from './generate-product-description/generate-product-description'
import { getQuoteOptions } from './get-options/get-quote-options'
import { getObjectLabels } from '../../util/get-object-labels/get-object-labels'

const TRAILING_LABEL_SUFFIX = / \([^()]*\)$/

/**
 * Splits a work-order sub-item name into the translatable base and the trailing label suffix
 * that `getItemLabel` appends (quantity, dimensions), e.g. "Doors (1 side) and Frame (1)" →
 * "Doors (1 side) and Frame" + " (1)". Only the last parenthesised group at the end counts as
 * the suffix, so a rate label with its own parentheses stays whole (ENG-2416).
 */
export function splitSubItemName(name: string): { base: string; suffix: string } {
  const match = TRAILING_LABEL_SUFFIX.exec(name)
  if (!match || match.index === 0) return { base: name, suffix: '' }
  return { base: name.substring(0, match.index), suffix: match[0] }
}

/**
 * Walks a quote document and returns the set of unique free-text strings
 * that are worth translating (crew notes, client notes, descriptions).
 * Only English-looking strings (containing at least one letter) are included.
 */
export function extractTranslatableContent(quote: QuoteDocument, options?: OptionsDocument): Set<string> {
  const strings = new Set<string>()
  const HAS_ALPHA_CHARACTER = /[a-z]/i

  function add(s: string | null | undefined) {
    if (s && typeof s === 'string' && s.trim() && HAS_ALPHA_CHARACTER.test(s)) {
      strings.add(s)
    }
  }

  // Document-level notes
  add(quote.internal_note)
  add(typeof quote.client_note === 'string' ? quote.client_note : null)

  // Product description (the "Product Description" card on the work order)
  const productDefault = quote.productString?.default
  const productCustom = quote.productString?.custom
  add(typeof productDefault === 'string' ? productDefault : null)
  add(typeof productCustom === 'string' ? productCustom : null)

  // Additional Work products (same card, its own overridable value)
  const additionalProductDefault = quote.additionalProductString?.default
  const additionalProductCustom = quote.additionalProductString?.custom
  add(typeof additionalProductDefault === 'string' ? additionalProductDefault : null)
  add(typeof additionalProductCustom === 'string' ? additionalProductCustom : null)

  // Area names, notes + substrate notes
  for (const area of Object.values(quote.areas ?? {})) {
    add(area.area_label?.value)
    add(area.internal_note)
    add(typeof area.client_note === 'string' ? area.client_note : null)

    for (const substrateList of Object.values(area.substrates ?? {})) {
      const list = Array.isArray(substrateList) ? substrateList : []
      for (const substrate of list) {
        add(substrate.client_label)
        add((substrate.rate as any)?.label)
        add((substrate.rate as any)?.client_label)
        add(substrate.notes)
        add(substrate.rate?.areaReportText)
        add((substrate.rate as any)?.report_text)
        for (const material of (substrate as any).materials ?? []) {
          add(material?.description)
        }
      }
    }
  }

  // Line item names, descriptions, notes, and material labels
  for (const lineItem of Object.values(quote.lineItems ?? {})) {
    add(lineItem.name)
    add(lineItem.description)
    add(lineItem.crew_note)
    add(lineItem.client_note)
    for (const material of lineItem.materials ?? []) {
      add(material?.label)
    }
  }

  if (options) {
    addWorkOrderRenderStrings({ quote, options, add })
  }

  return strings
}

function addWorkOrderRenderStrings({
  quote,
  options,
  add
}: {
  quote: QuoteDocument
  options: OptionsDocument
  add: (value: string | null | undefined) => void
}) {
  if (!quote?.totals) {
    return
  }

  const details = getDetails({ quote, options })
  const quoteOptions = getQuoteOptions({ quote, options })
  const productNote = getWorkOrderProductNote({ quote, options, details })
  add(productNote)

  const objectLabels = getObjectLabels({ options })
  add(objectLabels.labour.value)
  add(objectLabels.labour.plural)

  // The work order renders sub-item descriptions/crew-notes as *composed* strings
  // (product fields + prep/labor + label), built at render time by getCrewDescription.
  // useTranslatedNoteContent hashes those composed strings and looks them up in
  // quote.translations, so the pre-bake must produce the exact same strings or the
  // hashes miss and the PDF falls back to a live (race-prone) translation.
  //
  // WorkOrderView renders getItems across both views and several sections. Generate only the
  // currently configured showValues state for each view; changing that setting saves the quote
  // and bakes the newly-active variant. The old implementation generated both states on every
  // save, doubling composed-string translation volume for variants no user could see.
  const views: QuoteItemView[] = ['area', 'substrate']
  // 'bid' renders 'added-options' too (see WorkOrderTable); 'options' is the crew options table.
  // 'pending' covers the painter's Additional Work list (buildPainterAdditionalWork), whose
  // not-yet-approved items render this same composed area/substrate description via
  // useQuoteTranslator/mt() -- without it here, that string never gets a content-hash entry and
  // silently falls back to English for painters, even though the identical 'additional' (approved)
  // variant translates fine. Keep the pending section, while limiting each view to its active
  // showValues state below.
  const sections: QuoteItemSection[] = ['bid', 'added-options', 'additional', 'pending', 'options']

  for (const view of views) {
    for (const section of sections) {
      const showValues =
        view === 'area' ? quoteOptions.showWorkOrderAreaValues : quoteOptions.showWorkOrderSubstrateValues
      let items
      try {
        items = getItems({
          quote,
          options,
          section,
          view,
          consumer: 'crew',
          // Mirror WorkOrderTable's getItems call exactly so the composed
          // description strings (and their content hashes) match what renders.
          overrideOptions: { showQuantities: true },
          showLineItems: true,
          showValues,
          hideDimensions: quoteOptions.hideWorkOrderDimensions,
          lang: 'en'
        })
      } catch {
        // A malformed section/view shouldn't abort the whole pre-bake.
        continue
      }

      items.forEach((item) => {
        add(getItemName(item))
        add(getItemDescription(item))
        add(item.additionalFields?.crewNote)
        add(item.additionalFields?.clientNote)
        ;(item.subItems ?? []).forEach((subItem) => {
          const fullName = String(subItem.name ?? '')
          const { base: baseName } = splitSubItemName(fullName)

          add(fullName)
          add(baseName)
          add(subItem.crewNote)
          add(subItem.description)
          add(subItem.reportText)
        })
      })
    }
  }
}

// Item name/description can be a plain string or a customizable { useCustom, custom, default }
// object depending on the item type; this captures just the shape these helpers read.
type CustomizableField = string | { useCustom?: boolean; custom?: string; default?: string } | null | undefined
interface TranslatableItem {
  name?: CustomizableField
  description?: CustomizableField
}

function getItemName(item: TranslatableItem) {
  if (!item?.name) return ''
  if (typeof item.name === 'string') return item.name
  return item.name.useCustom ? item.name.custom : item.name.default
}

function getItemDescription(item: TranslatableItem) {
  if (!item?.description) return ''
  if (typeof item.description === 'string') return item.description
  return item.description.useCustom ? item.description.custom : item.description.default
}

function getWorkOrderProductNote({
  quote,
  options,
  details
}: {
  quote: QuoteDocument
  options: OptionsDocument
  details: ReturnType<typeof getDetails>
}) {
  const productNote = details.notes.products.useCustom
    ? (details.notes.products.custom as string)
    : (details.notes.products.default as string)
  // Mirrors the work order views: an Additional Work override wins over the generated text, so the
  // string we pre-translate is the one that actually renders.
  const additionalWorkNote = details.notes.additionalProducts?.useCustom
    ? (details.notes.additionalProducts.custom as string)
    : (details.notes.additionalProducts?.default as string) ??
      generateProductDescription({ quote, options, section: 'additional' })

  if (!additionalWorkNote.length) {
    return productNote
  }

  return productNote + `${productNote.length ? '\n' : ''}<b>Additional Work Products:</b>` + '\n' + additionalWorkNote
}
